/**
 * UX-only pre-check: does this *look* like an IP address or CIDR range?
 *
 * The server is the authority — it parses with Python's
 * `ipaddress.ip_network(strict=False)`. This deliberately errs permissive so it
 * never rejects something Python accepts: a false accept surfaces a readable
 * 422 from the server, while a false reject silently disables Save with no
 * override.
 */

const isPrefixValid = (prefix: string | undefined, max: number): boolean =>
  prefix === undefined || (/^\d{1,3}$/.test(prefix) && Number(prefix) <= max);

// Each octet 0-255 with no leading zeros (Python rejects `01.02.03.04`).
const isIpv4Octet = (octet: string): boolean =>
  /^(0|[1-9]\d{0,2})$/.test(octet) && Number(octet) <= 255;

// Permissive: hex groups, `::`, plus an optional trailing dotted-quad for the
// IPv4-mapped form (`::ffff:192.0.2.1`) that dual-stack proxies put in
// X-Forwarded-For — which is exactly what the enforcement middleware compares.
// Zone ids (`fe80::1%eth0`) are allowed: Python's `ip_network` has accepted
// scoped IPv6 since 3.9, and platform-api runs 3.12.
const IPV6_ADDRESS = /^[0-9a-fA-F:]+(\.\d{1,3}){0,3}(%[0-9A-Za-z._-]+)?$/;

export const isLikelyIpOrCidr = (value: string): boolean => {
  const parts = value.split('/');
  if (parts.length > 2) return false;

  const [address, prefix] = parts;
  if (!address) return false;

  if (address.includes(':')) {
    return IPV6_ADDRESS.test(address) && isPrefixValid(prefix, 128);
  }

  const octets = address.split('.');
  if (octets.length !== 4 || !octets.every(isIpv4Octet)) return false;

  // IPv4 takes a dotted netmask as well as a prefix length —
  // `10.0.0.0/255.255.255.0`, and even the inverted `10.0.0.0/0.0.0.255`, are
  // valid to ip_network(strict=False). Don't require a contiguous mask;
  // Python doesn't either, and a false reject is the costly direction.
  if (prefix !== undefined && prefix.includes('.')) {
    const maskOctets = prefix.split('.');
    return maskOctets.length === 4 && maskOctets.every(isIpv4Octet);
  }

  return isPrefixValid(prefix, 32);
};
